import { narrativeStructure } from '../data/narrativeStructure';
import { NarrativeState } from '../types/narrative';

interface VariantData {
  choiceText: string;
  npcResponse: string;
}

/**
 * Get variant data for S1_Player_Declaration from all three parent paths
 */
export function getNode2Variants(narrativeState: NarrativeState): Record<string, VariantData[]> {
  const variants: Record<string, VariantData[]> = {
    '1A': [],
    '1B': [],
    '1C': [],
  };

  // Get data from S1_Reaction_A
  const node1A = narrativeState.nodes['S1_Reaction_A'] || narrativeStructure['S1_Reaction_A'];
  if (node1A?.choices) {
    variants['1A'] = node1A.choices.slice(0, 2).map((choice) => ({
      choiceText: choice.text || '',
      npcResponse: choice.npcResponse || '',
    }));
  }

  // Get data from S1_Reaction_B
  const node1B = narrativeState.nodes['S1_Reaction_B'] || narrativeStructure['S1_Reaction_B'];
  if (node1B?.choices) {
    variants['1B'] = node1B.choices.slice(0, 2).map((choice) => ({
      choiceText: choice.text || '',
      npcResponse: choice.npcResponse || '',
    }));
  }

  // Get data from S1_Reaction_C
  const node1C = narrativeState.nodes['S1_Reaction_C'] || narrativeStructure['S1_Reaction_C'];
  if (node1C?.choices) {
    variants['1C'] = node1C.choices.slice(0, 2).map((choice) => ({
      choiceText: choice.text || '',
      npcResponse: choice.npcResponse || '',
    }));
  }

  return variants;
}

/**
 * Get variant data for B-Plot Reflection nodes from their corresponding Engagement nodes
 */
export function getBPlotVariants(nodeId: string, narrativeState: NarrativeState): VariantData[] {
  let sourceNodeId: string | null = null;

  // Map reflection nodes to their engagement nodes
  switch (nodeId) {
    case 'S2A_BPlot_Reflect':
      sourceNodeId = 'S2A_BPlot_Engage';
      break;
    case 'S2B_BPlot_Reflect':
      sourceNodeId = 'S2B_BPlot_Engage';
      break;
    case 'S2C_BPlot_Reflect':
      sourceNodeId = 'S2C_BPlot_Engage';
      break;
  }

  if (!sourceNodeId) return [];

  const sourceNode = narrativeState.nodes[sourceNodeId] || narrativeStructure[sourceNodeId];
  if (!sourceNode?.choices) return [];

  return sourceNode.choices.slice(0, 4).map((choice) => ({
    choiceText: choice.text || '',
    npcResponse: choice.npcResponse || '',
  }));
}

/**
 * Format variant data as a markdown table for Node2
 */
export function formatNode2VariantTable(variants: Record<string, VariantData[]>): string {
  let table = '| Choice | 1A | 1B | 1C |\n';
  table += '|--------|----|----|----|\n';

  for (let i = 0; i < 2; i++) {
    table += `| Choice ${i + 1} | `;

    ['1A', '1B', '1C'].forEach((path, pathIndex) => {
      const variant = variants[path][i];
      if (variant && (variant.choiceText || variant.npcResponse)) {
        const choiceText = variant.choiceText ? `**${variant.choiceText.toUpperCase()}**` : '';
        const npcResponse = variant.npcResponse
          ? `<br/><span style="color: #e5e7eb">${variant.npcResponse}</span>`
          : '';
        table += `${choiceText}${npcResponse}`;
      } else {
        table += '-';
      }

      if (pathIndex < 2) table += ' | ';
    });

    table += ' |\n';
  }

  return table;
}

/**
 * Format variant data as a markdown table for B-Plot nodes
 */
export function formatBPlotVariantTable(variants: VariantData[]): string {
  let table = '| Choice | Variant |\n';
  table += '|--------|--------|\n';

  variants.forEach((variant, index) => {
    const choiceText = variant.choiceText ? `**${variant.choiceText.toUpperCase()}**` : '';
    const npcResponse = variant.npcResponse
      ? `<br/><span style="color: #e5e7eb">${variant.npcResponse}</span>`
      : '';
    const content = choiceText || npcResponse ? `${choiceText}${npcResponse}` : '-';

    table += `| Choice ${index + 1} | ${content} |\n`;
  });

  return table;
}

/**
 * Check if a node should display a variant table
 */
export function shouldShowVariantTable(nodeId: string): boolean {
  const nodesWithTables = [
    'S1_Player_Declaration',
    'S2A_BPlot_Reflect',
    'S2B_BPlot_Reflect',
    'S2C_BPlot_Reflect',
  ];

  return nodesWithTables.includes(nodeId);
}
